import React, { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { applyFilterCategories, unselectFilterCategoryOptions } from '../../../../../../actions/searchActions';
import styles from './styles.module.css';
import HoverTooltip from '../../../../common/Tooltip';
import { TooltipIcon } from '../../../../../../assets/icons/Tooltip.icon';

const formatCurrency = (value) => {
  if (value == null) return '';
  const n = Number(value) || 0;
  const num = n.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `$ ${num}`;
};

const parseAmount = (text) => {
  if (typeof text === 'number') return text;
  if (typeof text !== 'string') return null;
  const digits = text.replace(/\./g, '').replace(/[^0-9]/g, '');
  const num = parseInt(digits, 10);
  return Number.isNaN(num) ? null : num;
};

const findCategoryIndexByID = (filterCategory, ids) => {
  if (!Array.isArray(filterCategory)) return -1;
  const toKey = (v) => (v == null ? '' : String(v));
  const list = ids == null ? [] : Array.isArray(ids) ? ids : [ids];
  if (!list.length) return -1;
  const idsSet = new Set(list.map(toKey));
  const idOf = (cat) => {
    if (!cat) return undefined;
    if (cat.ID !== undefined) return cat.ID;
    if (cat.Id !== undefined) return cat.Id;
    return cat.id;
  };
  return filterCategory.findIndex((cat) => idsSet.has(toKey(idOf(cat))));
};

function SliderFilterBase({
  filterCategory,
  unselectFilterCategoryOptions,
  applyFilterCategories,
  categoryId = null,
  matchIds = null,
  title,
  description,
  infoTooltip,
  ariaLabel,
  helperPrefix = 'Apresentando resultados a partir de:',
  thumbPx = 28,
  useStagingMode = false,
  onStageChange = null,
}) {
  const [stagedIndex, setStagedIndex] = React.useState(null);
  const categoryIndex = useMemo(() => {
    const ids = matchIds != null ? matchIds : categoryId;
    return findCategoryIndexByID(filterCategory, ids);
  }, [filterCategory, categoryId, matchIds]);

  const steps = useMemo(() => {
    if (categoryIndex < 0) return [];
    const cat = filterCategory[categoryIndex];
    if (!cat || !Array.isArray(cat.Categories)) return [];

    const mapped = cat.Categories.map((opt, optionIndex) => {
      const numeric = parseAmount((opt && (opt.Value || opt.Name)) || null);
      return {
        optionIndex,
        numeric,
        display: (opt && opt.Name) || formatCurrency(numeric),
        selected: Boolean(opt && opt.selected),
      };
    }).filter((x) => x.numeric != null);

    mapped.sort((a, b) => a.numeric - b.numeric);
    return mapped;
  }, [filterCategory, categoryIndex]);



  const currentIndex = useMemo(() => {
    if (!steps.length) return 0;

    if (useStagingMode && stagedIndex !== null) {
      return stagedIndex;
    }

    const selectedIndices = steps.map((s, i) => (s.selected ? i : -1)).filter((i) => i >= 0);
    if (!selectedIndices.length) return 0;
    return Math.max(...selectedIndices);
  }, [steps, useStagingMode, stagedIndex]);

  const onChange = useCallback(
    (e) => {
      if (categoryIndex < 0 || !steps.length) return;
      const desired = parseInt(e.target.value, 10) || 0;

      if (useStagingMode) {
        setStagedIndex(desired);

        if (onStageChange) {
          onStageChange({
            categoryIndex,
            selectedIndex: desired,
            step: steps[desired],
          });
        }
      } else {
        steps.forEach((s, i) => {
          const shouldBeSelected = i === desired;
          if (Boolean(s.selected) !== shouldBeSelected) {
            unselectFilterCategoryOptions('category', categoryIndex, s.optionIndex);
          }
        });

        applyFilterCategories();
      }
    },
    [categoryIndex, steps, unselectFilterCategoryOptions, applyFilterCategories, useStagingMode, onStageChange]
  );

  if (categoryIndex < 0 || !steps.length) return null;

  const current = steps[currentIndex];
  const progress = steps.length > 1 ? currentIndex / (steps.length - 1) : 0;
  const percent = Math.round(progress * 100);

  const formatted = formatCurrency(current && current.numeric);
  const numberOnly = formatted.replace(/^\$\s*/, '');

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          {title ? <span className={styles.title}>{title}</span> : null}
          {infoTooltip ? (
            <HoverTooltip content={infoTooltip}>
              <TooltipIcon />
            </HoverTooltip>
          ) : null}
        </div>
        {description ? <p className={styles.description}>{description}</p> : null}
      </div>

      <HoverTooltip
        className={styles.sliderWrap}
        content={formatted}
        anchorPercent={percent}
        anchorProgress={progress}
        thumbPx={thumbPx}
      >
        <input
          className={styles.slider}
          type="range"
          min={0}
          max={Math.max(steps.length - 1, 0)}
          step={1}
          value={currentIndex}
          onChange={onChange}
          aria-label={ariaLabel || title}
          style={{ '--percent': `${percent}%` }}
        />
      </HoverTooltip>

      <div className={styles.helper}>
        <span>{helperPrefix}</span>
        <span className={styles.value}>
          <span className={styles.currencySymbol}>$</span> {numberOnly}
        </span>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  filterCategory: state.search.filterCategory,
});

export default connect(mapStateToProps, { unselectFilterCategoryOptions, applyFilterCategories })(SliderFilterBase);



// WEBPACK FOOTER //
// ./src/views/v2/search/components/filter/components/SliderFilter.jsx